import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { mcaI20Logo } from "../../../Assets";
import KenLoader from "../../../GlobalComponents/KenLoader";
import { useToast } from "../../../Helpers/Contexts/toastContext";
import { verifyLogin } from "../../../Utils/apiService";
import routes from "../../../Utils/routes";

type LoginFormInputs = {
  username: string;
  password: string;
};

const Login: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();

  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onSubmit = async (data: LoginFormInputs) => {
    const { username, password } = data;
    const payload = { email: username, password };
    try {
      setLoading(true);
      const res = await verifyLogin(payload);
      if (res?.data?.success) {
        const userData = res.data.data?.data;
        const recordType = userData?.recordType;

        if (recordType === "Team Owner") {
          // Team Owner flow
          localStorage.setItem("isTeamAuthenticated", "true");
          localStorage.setItem("teamId", userData?.AccountId);
          navigate(routes.teamDashboard);
          return;
        } else if (recordType === "Admin") {
          // Admin flow
          localStorage.setItem("userDetails", JSON.stringify(userData));
          localStorage.setItem("isAuthenticated", "true");
          navigate(routes.dashboard);
          return;
        } else {
          // Unknown role fallback
          showToast("error", "Unauthorized access: unknown user role.");
        }
      }
    } catch (err: any) {
      console.error("Login error:", err);
      showToast(
        "error",
        err?.response?.data?.message || "Login failed. Try again."
      );
    } finally {
      setLoading(false);
    }
  };
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      {loading ? (
        <KenLoader />
      ) : (
        <Box
          height="100vh"
          display="flex"
          flexDirection={"column"}
          alignItems="center"
          justifyContent="center"
          sx={{ backgroundColor: "#09090B", position: "relative" }}
        >
          <Box
            sx={{
              background: "#E97D45",
              filter: "blur(50vw)",
              width: "50vw",
              height: "50vw",
              borderRadius: "50vw",
              opacity: "0.3",
              position: "absolute",
              top: "-25vw",
              right: "-25vw",
            }}
          />
          <Box
            sx={{
              background: "#E97D45",
              filter: "blur(50vw)",
              width: "50vw",
              height: "50vw",
              borderRadius: "50vw",
              opacity: "0.3",
              position: "absolute",
              bottom: "-25vw",
              left: "-10vw",
            }}
          />
          <Box sx={{ width: "160px", marginBottom: "60px" }}>
            <img
              src={mcaI20Logo}
              style={{ width: "160px", height: "auto" }}
              alt="MCA Logo"
            />
          </Box>
          <Paper
            sx={{
              p: "64px 48px",
              maxWidth: "600px",
              width: "100%",
              bgcolor: "#0D0D0D",
              border: "1px solid rgba(255, 255, 255, 0.20)",
              borderRadius: "12px",
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: "*email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Enter email ID"
                      fullWidth
                      error={!!errors.username}
                      helperText={errors.username?.message}
                      FormHelperTextProps={{ sx: { fontSize: "11px" } }}
                      sx={{
                        height: "60px",
                        "& .MuiOutlinedInput-root": {
                          height: "60px",
                        },
                        "& label": {
                          color: "#B5B3B3",
                          fontSize: "14px",
                          fontWeight: "400",
                        },
                        "& label.Mui-focused": {
                          color: "#B5B3B3",
                        },
                      }}
                      InputProps={{
                        sx: {
                          "&.Mui-focused": { color: "#fff" },
                          height: "60px",
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  rules={{ required: "Password is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Enter password"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      FormHelperTextProps={{ sx: { fontSize: "11px" } }}
                      sx={{
                        height: "60px",
                        "& .MuiOutlinedInput-root": {
                          height: "60px",
                        },
                        "& label": {
                          color: "#B5B3B3",
                          fontSize: "14px",
                          fontWeight: "400",
                        },
                        "& label.Mui-focused": {
                          color: "#B5B3B3",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ color: "#fff" }} />
                              ) : (
                                <Visibility sx={{ color: "#fff" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          height: "60px",
                        },
                      }}
                    />
                  )}
                />
                {/* <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: "16px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "500", color: "#fff" }}
                  >
                    Forgot password?
                  </Typography>
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "500", color: "#fff" }}
                  >
                    Contact admin
                  </Typography>
                </Box> */}

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    background: "#ffff",
                    ":hover": { background: "#ffff" },
                    color: "#0D0D0D",
                    height: "48px",
                    borderRadius: "8px",
                    fontSize: "18px",
                    fontWeight: "500",
                  }}
                >
                  Login
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default Login;
